import { useState, useEffect, useCallback, useRef } from "react";
import { TextField, InlineStack, Spinner, Icon } from "@medentic/polaris-react";
import { CheckIcon, AlertTriangleIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { URLLinkBuilder } from "@shared/lib";

type EventSlugEntityType = "event" | "instructor" | "venue" | "brand";

interface EventSlugFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  healthcareCompanyId: string | null;
  entityId?: string;
  entityType: EventSlugEntityType;
  error?: string;
  helpText?: string;
  disabled?: boolean;
  requiredIndicator?: boolean;
  placeholder?: string;
  prefix?: string;
}

interface SlugAvailabilityResponse {
  slug: string;
  exists: boolean;
  id?: string;
}

type ValidationState = "idle" | "checking" | "valid" | "invalid" | "error";

// Default prefixes for TextField (shorter version)
const DEFAULT_PREFIXES: Record<EventSlugEntityType, string> = {
  event: URLLinkBuilder.getSlugPrefix("event"),
  instructor: URLLinkBuilder.getSlugPrefix("expert"),
  venue: URLLinkBuilder.getSlugPrefix("venue"),
  brand: URLLinkBuilder.getSlugPrefix("brand"),
};

// API endpoint paths for each entity type
const API_PATHS: Record<EventSlugEntityType, string> = {
  event: "events",
  instructor: "instructors",
  venue: "venues",
  brand: "brands",
};

/**
 * Transforms a string into a valid URL slug
 * - Converts to lowercase
 * - Replaces spaces and underscores with hyphens
 * - Removes special characters (keeps alphanumeric and hyphens)
 * - Removes consecutive hyphens
 * Note: Leading/trailing hyphens are allowed during typing, but validated later
 */
const transformToSlug = (input: string): string => {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/_/g, "-") // Replace underscores with hyphens
    .replace(/[^a-z0-9-]/g, "") // Remove any character that's not alphanumeric or hyphen
    .replace(/-{2,}/g, "-"); // Replace consecutive hyphens with single hyphen
};

/**
 * Validates if a slug contains only allowed characters (client-side check)
 */
const isValidSlugFormat = (slug: string): boolean => {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
};

export default function EventSlugField({
  label,
  value,
  onChange,
  healthcareCompanyId,
  entityId,
  entityType,
  error,
  helpText,
  disabled = false,
  requiredIndicator = false,
  placeholder,
  prefix,
}: EventSlugFieldProps) {
  const { t } = useTranslation();
  const { GET } = useApiRequest();
  const [localSlug, setLocalSlug] = useState(value || "");
  const [validationState, setValidationState] = useState<ValidationState>("idle");
  const [validationError, setValidationError] = useState<string | undefined>();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Use provided placeholder or default URL prefix based on entity type
  const effectivePlaceholder = placeholder || '';
  
  // Use provided prefix or default prefix based on entity type
  const effectivePrefix = prefix !== undefined ? prefix : DEFAULT_PREFIXES[entityType];

  // Get label based on entity type
  const getDefaultLabel = () => {
    switch (entityType) {
      case "event":
        return t('events.component.slug_picker.event.title');
      case "instructor":
        return t('events.component.slug_picker.instructor.title');
      case "brand":
        return t('events.component.slug_picker.brand.title');
      case "venue":
        return t('events.component.slug_picker.venue.title');
      default:
        return t('events.component.slug_picker.title');
    }
  };

  // Get help text based on entity type and validation state
  const getDefaultHelpText = () => {
    switch (entityType) {
      case "event":
        return t('events.component.slug_picker.event.help_text');
      case "instructor":
        return t('events.component.slug_picker.instructor.help_text');
      case "brand":
        return t('events.component.slug_picker.brand.help_text');
      case "venue":
        return t('events.component.slug_picker.venue.help_text');
      default:
        return t('events.component.slug_picker.help_text');
    }
  };

  const effectiveLabel = label || getDefaultLabel();
  const baseHelpText = helpText || getDefaultHelpText();

  // Enhance help text based on validation state
  const effectiveHelpText = (() => {
    if (validationState === "checking") {
      return t('events.component.slug_picker.checking');
    }
    if (validationState === "valid" && localSlug.trim()) {
      return t('events.component.slug_picker.slug_free');
    }
    return baseHelpText;
  })();

  // Sync local slug with prop value when it changes
  useEffect(() => {
    setLocalSlug(value || "");
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Check slug availability
  const checkSlugAvailability = useCallback(
    async (slug: string) => {
      if (!slug.trim() || !healthcareCompanyId) {
        setValidationState("idle");
        setValidationError(undefined);
        return;
      }

      // Client-side validation first
      if (!isValidSlugFormat(slug)) {
        setValidationState("invalid");
        setValidationError(t('events.component.slug_picker.invalid_format'));
        return;
      }

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setValidationState("checking");
      setValidationError(undefined);

      try {
        // Build URL based on entity type using the API_PATHS mapping
        const entityPath = API_PATHS[entityType];
        const url = `/api/healthcare-companies/${healthcareCompanyId}/e/${entityPath}/slug-availability/${encodeURIComponent(
          slug
        )}`;
/*
        if (entityType === EntityType.INSTRUCTOR) {
          url = `/api/healthcare-companies/${healthcareCompanyId}/e/instructors/slug-availability/${encodeURIComponent(
            slug
          )}`;
        } else if (entityType === EntityType.VENUE) {
          url = `/api/healthcare-companies/${healthcareCompanyId}/e/venues/slug-availability/${encodeURIComponent(
            slug
          )}`;
        }*/

        const response: SlugAvailabilityResponse = await GET(url, false, false, true);

        // Check if the slug exists
        if (response.exists) {
          // If editing an existing entity and the slug belongs to it, it's valid
          if (entityId && response.id && response.id === entityId) {
            setValidationState("valid");
            setValidationError(undefined);
          } else {
            // Slug is taken by another entity (or creating new entity and slug exists)
            setValidationState("invalid");
            setValidationError(t('events.component.slug_picker.slug_taken'));
          }
        } else {
          // Slug is available
          setValidationState("valid");
          setValidationError(undefined);
        }
      } catch (error) {
        const err = error as {
          name?: string;
          response?: { data?: { error?: { display?: string }; message?: string } };
        };
        // Ignore aborted requests
        if (err.name === "AbortError") {
          return;
        }

        console.error("Slug validation error:", err);
        console.error("Error response:", err?.response?.data);
        setValidationState("error");
        
        // Try to get the actual error message from the API response
        const errorData = err?.response?.data;
        const apiErrorMessage = errorData?.error?.display || errorData?.message;
        
        setValidationError(apiErrorMessage || t('events.component.slug_picker.api_error'));
      } finally {
        abortControllerRef.current = null;
      }
    },
    [healthcareCompanyId, entityId, entityType, GET, t]
  );

  // Handle slug change with debouncing
  const handleSlugChange = useCallback(
    (newValue: string) => {
      // Transform input into a valid slug
      const transformedValue = transformToSlug(newValue);

      setLocalSlug(transformedValue);
      onChange(transformedValue);

      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Reset validation state when user starts typing
      if (validationState !== "idle" && validationState !== "checking") {
        setValidationState("idle");
        setValidationError(undefined);
      }

      // Set up new debounced check
      if (transformedValue) {
        debounceTimerRef.current = setTimeout(() => {
          checkSlugAvailability(transformedValue);
        }, 500);
      } else {
        setValidationState("idle");
        setValidationError(undefined);
      }
    },
    [onChange, validationState, checkSlugAvailability]
  );

  // Determine display error (external error takes precedence)
  const displayError = error || validationError;

  // Determine suffix based on validation state
  const suffix =
    validationState === "checking" ? (
      <Spinner size="small" />
    ) : validationState === "valid" ? (
      <Icon source={CheckIcon} tone="success" />
    ) : validationState === "invalid" || validationState === "error" ? (
      <Icon source={AlertTriangleIcon} tone="critical" />
    ) : undefined;

  return (
    <InlineStack gap="200" blockAlign="center">
      <div style={{ flexGrow: 1 }}>
        <TextField
          label={effectiveLabel}
          value={localSlug}
          onChange={handleSlugChange}
          error={displayError}
          helpText={effectiveHelpText}
          disabled={disabled}
          requiredIndicator={requiredIndicator}
          placeholder={effectivePlaceholder}
          prefix={effectivePrefix}
          suffix={suffix}
          autoComplete="off"
        />
        {/* Accessibility: Announce validation state changes to screen readers */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {validationState === "checking" && t('events.component.slug_picker.checking')}
          {validationState === "valid" && localSlug && t('events.component.slug_picker.slug_free')}
          {validationState === "invalid" && validationError}
          {validationState === "error" && validationError}
        </div>
      </div>
    </InlineStack>
  );
}
